import { formatLabel } from './formatLabel.ts';
import { formatProduct } from './formatProduct.ts';
import { getProductAppIcon } from './getProductAppIcon.ts';
import { getSmartLinkIcon } from './smartLinkIcon.ts';
import type { EnhancedSmartLink, SmartLinkIcon } from './smartLinkTypes.ts';

export const MAX_BYLINE_CONNECTION_ICONS = 3;

export type BylineConnectionIcon = {
	key: string;
	label: string;
	icon: SmartLinkIcon;
};

type RankedBylineConnectionIcon = {
	icon: BylineConnectionIcon;
	isUnconnected: boolean;
	occurrenceIndex: number;
};

const isUnconnectedSmartLink = (link: EnhancedSmartLink): boolean => link.access !== 'granted';

const toIcon = (link: EnhancedSmartLink, icon: SmartLinkIcon): BylineConnectionIcon => {
	const product = formatLabel(link.product);
	const isAppLogo = icon.kind === 'logo';

	return {
		// App logos collapse Jira / Jira Software and Atlas apps onto one chip.
		key: isAppLogo ? icon.key : product || icon.key,
		label: (isAppLogo ? formatProduct(link.product) : product) ?? icon.label ?? '',
		icon,
	};
};

const dedupeIcons = (icons: BylineConnectionIcon[]): BylineConnectionIcon[] => {
	const seenKeys = new Set<string>();
	const dedupedIcons: BylineConnectionIcon[] = [];

	for (const icon of icons) {
		if (!seenKeys.has(icon.key)) {
			seenKeys.add(icon.key);
			dedupedIcons.push(icon);
		}
	}

	return dedupedIcons;
};

const sortRankedIcons = (left: RankedBylineConnectionIcon, right: RankedBylineConnectionIcon) => {
	if (left.isUnconnected !== right.isUnconnected) {
		return Number(right.isUnconnected) - Number(left.isUnconnected);
	}

	return left.occurrenceIndex - right.occurrenceIndex;
};

// Ranks one direction: unconnected links first (they carry a connect prompt), then document or
// recency order.
const rankIcons = (links: EnhancedSmartLink[]): BylineConnectionIcon[] => {
	const rankedIcons: RankedBylineConnectionIcon[] = [];

	for (const link of links) {
		const icon = getProductAppIcon(link) ?? getSmartLinkIcon(link);
		if (!icon) {
			continue;
		}

		rankedIcons.push({
			icon: toIcon(link, icon),
			isUnconnected: isUnconnectedSmartLink(link),
			occurrenceIndex: link.occurrenceIndex,
		});
	}

	rankedIcons.sort(sortRankedIcons);
	return rankedIcons.map(({ icon }) => icon);
};

// Outgoing icons fill the stack first; incoming icons take the leftover slots. Products are deduped
// across both directions.
//
// The stack must not change after it paints, but the two directions resolve independently. Giving
// one direction precedence makes a stack it fills on its own final regardless of the other, so the
// trigger can paint without waiting. Outgoing gets precedence because it usually resolves first and
// its links are the page's own. The cost: an incoming connect prompt is hidden when outgoing links
// already fill the stack.
export const getBylineConnectionIcons = (
	links: EnhancedSmartLink[],
	additionalIcons: BylineConnectionIcon[] = [],
	incomingLinks: EnhancedSmartLink[] = [],
): BylineConnectionIcon[] =>
	dedupeIcons([...additionalIcons, ...rankIcons(links), ...rankIcons(incomingLinks)]).slice(
		0,
		MAX_BYLINE_CONNECTION_ICONS,
	);
